import {
    type DateRangeValueType,
} from "@lib/zod/dateTimePropEditor";

export interface BreakdownItem {
    method: string;
    total: number;
    count: number;
    formattedTotal: string;
}

export interface BreakdownData {
    paymentBreakdown: BreakdownItem[];
    grandTotal: {
        total: number;
        count: number;
        formattedTotal: string;
    };
    totalDeliveryFees: string;
    countDeliveries: number;
    platformSupportFeeBreakdown?: {
        label: string;
        info: string;
        total: number;
        formattedTotal: string;
    }[];
    dateRange: DateRangeValueType;
    currency?: string;
}

const style = (
    fontSizeRem: number,
    fontWeight: 'normal' | 'bold' = 'normal'
) => {
    return `
<style>
    @page {
        margin: 0 !important;
        size: 80mm auto;
    }
    *, *::before, *::after {
        box-sizing: border-box;
    }
    html, body {
        width: 100%;
        max-width: 100%;
        margin: 0 !important;
        padding: 0 !important;
        overflow-x: hidden;
    }
    body {
        font-size: ${fontSizeRem}rem;
        font-weight: ${fontWeight};
    }
    .print-container {
        width: 100%;
        max-width: 270px;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden;
        white-space: pre-wrap;
        overflow-wrap: normal;
        word-break: normal;
        container-type: inline-size;
    }
    .print-container h6,
    .print-container h5,
    .print-container h4,
    .print-container h3,
    .print-container p,
    .print-container div {
        margin: 0 !important;
        padding: 0 !important;
    }
    .print-container h6 {
        font-size: ${fontSizeRem}rem;
    }
    .print-container h5 {
        font-size: ${fontSizeRem * 1.2}rem;
    }
    .print-container h4 {
        font-size: ${fontSizeRem * 1.1}rem;
    }
    .print-container h3 {
        font-size: ${fontSizeRem}rem;
    }
    .print-container table {
        width: 100%;
        border-collapse: collapse;
        border-spacing: 0;
        margin: 0 !important;
        padding: 0 !important;
        font-size: inherit;
        font-weight: inherit;
    }
    .print-container td {
        margin: 0 !important;
        padding: 0 !important;
        vertical-align: top;
    }
    .print-container .bold{
        font-size: ${fontSizeRem * 1.2}rem;
        font-weight: bold;
    }
    .print-container .item-name{
        width: auto;
        font-size: ${fontSizeRem * 1.2}rem;
        vertical-align: top;
        font-weight: bold;
        white-space: pre-wrap;
        overflow-wrap: normal;
        word-break: normal;
    }
    .print-container .item-price {
        width: 1%;
        white-space: nowrap;
        vertical-align: top;
        //text-align: right;
    }
    .print-container .item-count {
        width: 1%;
        white-space: nowrap;
        vertical-align: top;
        //text-align: right;
        //padding-left: 8px !important;
    }
    hr {
        margin: 2px 0 !important;
        padding: 0 !important;
        border: none;
        border-top: 1px dashed #000;
    }
    .print-container .center {
        text-align: center;
    }
    .print-container .right {
        text-align: right;
    }
</style>
`
}

const getFormattedRange = (dateRange: DateRangeValueType) => {
    return `${new Date(dateRange.start ?? 0).toLocaleDateString('en-CA')} [${new Date(dateRange.start ?? 0).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit',
        hour12: false
    })}]\n${new Date(dateRange.end ?? 0).toLocaleDateString('en-CA')} [${new Date(dateRange.end ?? 0).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit',
        hour12: false
    })}]`;
}

export const formatChartBreakdown = (
    data: BreakdownData,
    fontSizeRem: number = 0.8,
    fontWeight: 'normal' | 'bold' = 'normal'
) => {
    let output = '';

    // Header
    output += `${getFormattedRange(data.dateRange)}\n`;
    output += `<hr>`;

    // Payment breakdown
    data.paymentBreakdown.forEach((item) => {
        output += `<div class="item-name">${item.method}</div>`;
        output += `<div class="item-price">${item.formattedTotal}</div>`;
        output += `<div class="item-count">Count: ${item.count}</div>`;
        output += `<hr>`;
    });

    // Total
    output += `<div class="item-name">TOTAL</div>`;
    output += `<div class="item-price">${data.grandTotal.formattedTotal}</div>`;
    output += `<div class="item-count">Count: ${data.grandTotal.count}</div>`;
    output += `<hr>`;

    // Delivery fees
    output += `<div>Delivery Fees: ${data.totalDeliveryFees}</div>`;
    output += `<div>Delivery Orders Count: ${data.countDeliveries}</div>`;
    output += `<hr>`;

    if (data.platformSupportFeeBreakdown?.length) {
        data.platformSupportFeeBreakdown.forEach((item) => {
            output += `<div>${item.label}: ${item.formattedTotal}</div>`;
            output += `<div>(${item.info})</div>`;
            output += `<hr>`;
        });
    }
    output += `<br>`;

    // Footer
    output += `${new Date().toLocaleString()}\n`;

    return `<html><head><meta charset="utf-8"></head><body>${style(fontSizeRem, fontWeight)}<div class="print-container">${output}</div></body></html>`;
};
